import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Bell, Plus, Trash2, Settings, Loader2, Phone, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import {
  apiService,
  NotificationRulesByEmail,
  EventNotificationRule,
  PhoneCapacityInfo,
  AdminEventTypeOption,
} from '../../services/api';

const PHONE_EVENT_TYPES = ['phone_number_purchased', 'phone_number_released'];
const CAPACITY_THRESHOLDS = [
  { value: '', label: 'Keine Bedingung' },
  { value: '10', label: '< 10% frei' },
  { value: '20', label: '< 20% frei' },
  { value: '30', label: '< 30% frei' },
];

export function SystemEventsPage() {
  // State
  const [rulesGroups, setRulesGroups] = useState<NotificationRulesByEmail[]>([]);
  const [phoneCapacity, setPhoneCapacity] = useState<PhoneCapacityInfo | null>(null);
  const [eventTypes, setEventTypes] = useState<AdminEventTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [capacityLoading, setCapacityLoading] = useState(true);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formEmail, setFormEmail] = useState('');
  const [formEventType, setFormEventType] = useState('');
  const [formLabel, setFormLabel] = useState('');
  const [formConditionThreshold, setFormConditionThreshold] = useState('');

  // Edit form state
  const [editingRule, setEditingRule] = useState<EventNotificationRule | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editConditionThreshold, setEditConditionThreshold] = useState('');

  // Delete state
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);

  // Capacity form
  const [capacityInput, setCapacityInput] = useState('');
  const [capacitySaving, setCapacitySaving] = useState(false);

  // Collapsed groups
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Data fetching
  const fetchRules = useCallback(async () => {
    try {
      setLoading(true);
      const [rules, types] = await Promise.all([
        apiService.getNotificationRules(),
        apiService.getAdminEventTypes(),
      ]);
      setRulesGroups(rules);
      setEventTypes(types);
    } catch {
      toast.error('Fehler beim Laden der Benachrichtigungsregeln');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCapacity = useCallback(async () => {
    try {
      setCapacityLoading(true);
      const cap = await apiService.getPhoneCapacity();
      setPhoneCapacity(cap);
      setCapacityInput(cap.total.toString());
    } catch {
      toast.error('Fehler beim Laden der Telefon-Kapazität');
    } finally {
      setCapacityLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
    fetchCapacity();
  }, [fetchRules, fetchCapacity]);

  // Handlers
  const handleCreateRule = async () => {
    if (!formEmail || !formEventType) {
      toast.error('E-Mail und Event-Typ sind erforderlich');
      return;
    }

    try {
      setSaving(true);
      const isPhoneEvent = PHONE_EVENT_TYPES.includes(formEventType);
      const conditionType = isPhoneEvent && formConditionThreshold ? 'phone_capacity_below' : undefined;
      const conditionValue = conditionType ? { threshold: Number(formConditionThreshold) } : undefined;

      await apiService.createNotificationRule({
        email: formEmail,
        eventType: formEventType,
        label: formLabel || undefined,
        conditionType,
        conditionValue,
      });

      toast.success('Regel erstellt');
      setDialogOpen(false);
      resetForm();
      fetchRules();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Erstellen der Regel';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRule = async () => {
    if (!editingRule) return;

    try {
      setSaving(true);
      const isPhoneEvent = PHONE_EVENT_TYPES.includes(editingRule.eventType);
      const conditionType = isPhoneEvent && editConditionThreshold ? 'phone_capacity_below' : null;
      const conditionValue = conditionType ? { threshold: Number(editConditionThreshold) } : null;

      await apiService.updateNotificationRule(editingRule.id, {
        label: editLabel || undefined,
        conditionType,
        conditionValue,
      });

      toast.success('Regel aktualisiert');
      setEditDialogOpen(false);
      setEditingRule(null);
      fetchRules();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Aktualisieren der Regel';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async () => {
    if (!deletingRuleId) return;

    try {
      await apiService.deleteNotificationRule(deletingRuleId);
      toast.success('Regel gelöscht');
      setDeleteDialogOpen(false);
      setDeletingRuleId(null);
      fetchRules();
    } catch {
      toast.error('Fehler beim Löschen der Regel');
    }
  };

  const handleToggleRule = async (ruleId: string) => {
    try {
      await apiService.toggleNotificationRule(ruleId);
      fetchRules();
    } catch {
      toast.error('Fehler beim Umschalten der Regel');
    }
  };

  const handleSetCapacity = async () => {
    const total = Number(capacityInput);
    if (isNaN(total) || total < 0) {
      toast.error('Bitte eine gültige Zahl eingeben');
      return;
    }

    try {
      setCapacitySaving(true);
      const cap = await apiService.setPhoneCapacity(total);
      setPhoneCapacity(cap);
      toast.success('Kapazität aktualisiert');
    } catch {
      toast.error('Fehler beim Setzen der Kapazität');
    } finally {
      setCapacitySaving(false);
    }
  };

  const resetForm = () => {
    setFormEmail('');
    setFormEventType('');
    setFormLabel('');
    setFormConditionThreshold('');
  };

  const openCreateDialog = (email?: string) => {
    resetForm();
    if (email) setFormEmail(email);
    setDialogOpen(true);
  };

  const openEditDialog = (rule: EventNotificationRule) => {
    setEditingRule(rule);
    setEditLabel(rule.label || '');
    setEditConditionThreshold(
      rule.conditionType === 'phone_capacity_below' && rule.conditionValue?.threshold
        ? rule.conditionValue.threshold.toString()
        : '',
    );
    setEditDialogOpen(true);
  };

  const toggleGroupCollapsed = (email: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  const capacityPercentage = phoneCapacity
    ? phoneCapacity.total > 0
      ? Math.round((phoneCapacity.assigned / phoneCapacity.total) * 100)
      : 0
    : 0;

  const capacityColor = phoneCapacity
    ? phoneCapacity.freePercentage < 10
      ? 'bg-red-500'
      : phoneCapacity.freePercentage < 20
        ? 'bg-orange-500'
        : phoneCapacity.freePercentage < 30
          ? 'bg-yellow-500'
          : 'bg-green-500'
    : 'bg-gray-300';

  return (
    <div className="flex flex-col gap-6 p-6 h-full overflow-auto">
      {/* Phone Capacity Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-lg">Telefonnummer-Kapazität</CardTitle>
          </div>
          <CardDescription>Systemweite Kapazität für Telefonnummern</CardDescription>
        </CardHeader>
        <CardContent>
          {capacityLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-3 w-full" />
            </div>
          ) : phoneCapacity ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">{phoneCapacity.assigned}</span>
                  {' / '}
                  <span className="font-semibold text-gray-900">{phoneCapacity.total}</span>
                  {' belegt '}
                  <span className={`font-semibold ${phoneCapacity.freePercentage < 20 ? 'text-red-600' : 'text-green-600'}`}>
                    ({phoneCapacity.freePercentage}% frei)
                  </span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all ${capacityColor}`}
                  style={{ width: `${Math.min(capacityPercentage, 100)}%` }}
                />
              </div>

              {/* Capacity input */}
              <div className="flex items-end gap-3">
                <div className="flex-1 max-w-xs">
                  <Label className="text-xs text-gray-500">Gesamtkapazität</Label>
                  <Input
                    type="number"
                    min={0}
                    value={capacityInput}
                    onChange={(e) => setCapacityInput(e.target.value)}
                    placeholder="z.B. 100"
                    className="h-9"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={handleSetCapacity}
                  disabled={capacitySaving}
                  className="h-9"
                >
                  {capacitySaving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Speichern
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Keine Daten verfügbar</p>
          )}
        </CardContent>
      </Card>

      {/* Notification Rules */}
      <Card className="flex-1">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-600" />
              <CardTitle className="text-lg">Benachrichtigungsregeln</CardTitle>
            </div>
            <Button size="sm" className="gap-2" onClick={() => openCreateDialog()}>
              <Plus className="w-4 h-4" />
              Neue Regel
            </Button>
          </div>
          <CardDescription>E-Mail-Benachrichtigungen für System-Events konfigurieren</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          ) : rulesGroups.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Noch keine Benachrichtigungsregeln erstellt.</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4 gap-2"
                onClick={() => openCreateDialog()}
              >
                <Plus className="w-4 h-4" />
                Erste Regel erstellen
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {rulesGroups.map((group) => {
                const isCollapsed = collapsedGroups.has(group.email);
                const activeCount = group.rules.filter((r) => r.isActive).length;

                return (
                  <div
                    key={group.email}
                    className="border rounded-lg overflow-hidden"
                  >
                    {/* Group header */}
                    <div
                      className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => toggleGroupCollapsed(group.email)}
                    >
                      <div className="flex items-center gap-3">
                        {isCollapsed ? (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="font-medium text-sm">{group.email}</span>
                        <Badge variant="secondary" className="text-xs">
                          {activeCount}/{group.rules.length} aktiv
                        </Badge>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="gap-1 h-7 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          openCreateDialog(group.email);
                        }}
                      >
                        <Plus className="w-3 h-3" />
                        Regel
                      </Button>
                    </div>

                    {/* Rules list */}
                    {!isCollapsed && (
                      <div className="divide-y">
                        {group.rules.map((rule) => (
                          <div
                            key={rule.id}
                            className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <Switch
                                checked={rule.isActive}
                                onCheckedChange={() => handleToggleRule(rule.id)}
                                className="shrink-0"
                              />
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm ${rule.isActive ? 'text-gray-900' : 'text-gray-400'}`}>
                                    {rule.label || rule.eventTypeLabel}
                                  </span>
                                  {rule.conditionLabel && (
                                    <Badge variant="outline" className="text-xs shrink-0">
                                      {rule.conditionLabel}
                                    </Badge>
                                  )}
                                </div>
                                {rule.label && (
                                  <p className="text-xs text-gray-400">{rule.eventTypeLabel}</p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => openEditDialog(rule)}
                              >
                                <Settings className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-red-500 hover:text-red-700"
                                onClick={() => {
                                  setDeletingRuleId(rule.id);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Rule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neue Benachrichtigungsregel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>E-Mail-Adresse</Label>
              <Input
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                placeholder="admin@example.com"
              />
            </div>

            <div>
              <Label>Event-Typ</Label>
              <Select value={formEventType} onValueChange={setFormEventType}>
                <SelectTrigger>
                  <SelectValue placeholder="Event-Typ wählen" />
                </SelectTrigger>
                <SelectContent>
                  {eventTypes.map((et) => (
                    <SelectItem key={et.type} value={et.type}>
                      {et.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Label (optional)</Label>
              <Input
                value={formLabel}
                onChange={(e) => setFormLabel(e.target.value)}
                placeholder="z.B. Monitoring-Alert"
              />
            </div>

            {PHONE_EVENT_TYPES.includes(formEventType) && (
              <div>
                <Label>Bedingung</Label>
                <Select
                  value={formConditionThreshold}
                  onValueChange={setFormConditionThreshold}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Keine Bedingung" />
                  </SelectTrigger>
                  <SelectContent>
                    {CAPACITY_THRESHOLDS.map((ct) => (
                      <SelectItem key={ct.value || 'none'} value={ct.value || 'none'}>
                        {ct.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Nur benachrichtigen wenn die systemweite Telefonnummer-Kapazität unter dem Schwellenwert liegt.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreateRule} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Rule Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regel bearbeiten</DialogTitle>
          </DialogHeader>
          {editingRule && (
            <div className="space-y-4 py-2">
              <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">{editingRule.email}</span>
                {' — '}
                {editingRule.eventTypeLabel}
              </div>

              <div>
                <Label>Label (optional)</Label>
                <Input
                  value={editLabel}
                  onChange={(e) => setEditLabel(e.target.value)}
                  placeholder="z.B. Monitoring-Alert"
                />
              </div>

              {PHONE_EVENT_TYPES.includes(editingRule.eventType) && (
                <div>
                  <Label>Bedingung</Label>
                  <Select
                    value={editConditionThreshold || 'none'}
                    onValueChange={(v) => setEditConditionThreshold(v === 'none' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Keine Bedingung" />
                    </SelectTrigger>
                    <SelectContent>
                      {CAPACITY_THRESHOLDS.map((ct) => (
                        <SelectItem key={ct.value || 'none'} value={ct.value || 'none'}>
                          {ct.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleUpdateRule} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regel löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Die Benachrichtigungsregel wird dauerhaft gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRule}
              className="bg-red-600 hover:bg-red-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
